import { ref, inject } from 'vue';
import { useStoreCart } from '@/stores/cart';
import { useStoreRestaurant } from '@/stores/restaurant';
import { useStoreOrder } from '@/stores/order';
import { useStoreApp } from '@/stores/app';
import { useStoreCheckout } from '@/stores/checkout';

const LOGGER_TAG = 'pay';
export function usePay() {
    const logger = inject('logger');
    let googlePayIsReady = ref(false);
    let cStore = useStoreCart();
    let rStore = useStoreRestaurant();
    let oStore = useStoreOrder();
    let aStore = useStoreApp();
    let checkoutStore = useStoreCheckout();

    let baseRequest = null;
    let cardPaymentMethod = null;
    let paymentsClient = null;

    async function loadGooglePayScript() {
        const script = document.createElement('script');
        script.src = 'https://pay.google.com/gp/p/js/pay.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            logger.info(LOGGER_TAG, 'Google Pay script loaded');
        };
        document.head.appendChild(script);
    }

    async function googlePayInit() {
        googlePayIsReady.value = true;
    }

    async function checkoutGooglePay() {
        let pos = oStore.pos;

        baseRequest = {
            apiVersion: 2,
            apiVersionMinor: 0
        };

        const tokenizationSpecification = {
            type: 'PAYMENT_GATEWAY',
            parameters: {
                'gateway': 'nexi',
                'gatewayMerchantId': pos.xpay_merchant
            }
        };

        const allowedCardNetworks = ["AMEX", "DISCOVER", "INTERAC", "JCB", "MASTERCARD", "VISA"];

        const allowedCardAuthMethods = ["PAN_ONLY", "CRYPTOGRAM_3DS"];

        const baseCardPaymentMethod = {
            type: 'CARD',
            parameters: {
                allowedAuthMethods: allowedCardAuthMethods,
                allowedCardNetworks: allowedCardNetworks
            }
        };

        cardPaymentMethod = Object.assign({
            tokenizationSpecification: tokenizationSpecification
        }, baseCardPaymentMethod);

        paymentsClient = new google.payments.api.PaymentsClient({ environment: pos.xpay_googlepay_test ? 'TEST' : 'PRODUCTION' });

        const isReadyToPayRequest = Object.assign({}, baseRequest);
        isReadyToPayRequest.allowedPaymentMethods = [baseCardPaymentMethod];

        let isReadyToPay = await paymentsClient.isReadyToPay(isReadyToPayRequest);
        logger.info(LOGGER_TAG, "Google Pay - isReadyToPay", isReadyToPay);
        if (isReadyToPay.result) {
            logger.info(LOGGER_TAG, "Google Pay - Ready to Pay");
        }

    }

    async function payRequest() {
        const paymentDataRequest = Object.assign({}, baseRequest);
        paymentDataRequest.allowedPaymentMethods = [cardPaymentMethod];

        paymentDataRequest.transactionInfo = {
            totalPriceStatus: 'FINAL',
            totalPrice: String(cStore.total), // (force string)
            currencyCode: rStore.restaurant.currency.code,
            countryCode: oStore.pos.country
        };

        paymentDataRequest.merchantInfo = {
            merchantName: rStore.restaurant.name,
            merchantId: oStore.pos.googlepay_merchant_id
        }
        
        logger.info(LOGGER_TAG, "Google Pay - paymentDataRequest", paymentDataRequest);
        let paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);
        // if using gateway tokenization, pass this token without modification
        //var jsonToken = paymentData.paymentMethodData.tokenizationData.token;
        logger.info("Google Pay - paymentData", paymentData);
        var jsonToken = JSON.stringify(paymentData);
        return jsonToken;
    };

    async function googlepayOrderValidation(onReady) {
        if (!aStore.isCordova) {
            return { valid: true };
        }

        // Real Android App: validate before starting requesting Google Pay token
        let formData = checkoutStore.form;
        let response = await oStore.act('order_validation', formData);
        return response;
    }

    return {
        googlePayIsReady,
        googlepayOrderValidation,
        loadGooglePayScript,
        googlePayInit,
        payRequest,
        checkoutGooglePay
    };
}